/* THE TRANSFORM SPEC — one definition, imported by both runtimes.
 *
 * PHASE 4, STEP 6. Jesse, 2026-08-12, on the step this file exists for:
 * **"This is the most important thing in the phase."**
 *
 *   *"The editor sends the ORIGINAL FILE BYTES plus a transform spec —
 *   {angle, crop:{x,y,w,h}} as fractions — and the one shared encoder
 *   applies the rotation and crop to the full-resolution original and
 *   does ONE resize from it per tier."*
 *
 * ── ⚠⚠ WHY IT IS A SHARED MODULE AND NOT A DOCUMENTED CONVENTION ────
 * The same reason `photo-ref.js` is one: a rule stated in two runtimes
 * is a rule that has already drifted, and that one HAD, measurably,
 * before anybody looked. This spec is worse than the ref grammar in one
 * specific way — **its drift is silent and produces a plausible
 * picture.** BUILD_NOTES named both traps before either could fire:
 *
 *   1. **THE CROP FRACTIONS ARE RELATIVE TO THE ROTATED BOUNDING BOX**,
 *      not to the original image. `editor.js:95` multiplies them by
 *      `ew`/`eh`, which come from `bbox(im.width, im.height, angle)`.
 *      A spec interpreted against the UNROTATED original crops the wrong
 *      region on every angled photo — and returns a real photograph of
 *      the right note, slightly wrong, with no error anywhere.
 *   2. **ROTATION EXPOSES CORNERS AND THEY ARE FILLED `#12291E`**
 *      (`editor.js:93,99`) — the app's panel green. Not white, not
 *      transparent. An encoder that fills them differently produces a
 *      different picture from the identical spec.
 *
 * Both are implemented HERE, once, so neither can be got wrong twice.
 *
 * ── ⚠ WHAT THIS FILE DELETES FROM THE EDITOR ───────────────────────
 * Three lossy operations, not one, read off `editor.js:87-102`:
 *   EXP=4200   the rotation baked onto a canvas sized to the rotated
 *              bounding box.                              RESAMPLE 1.
 *   OUT=3200   the cropped region drawn to a second canvas capped at a
 *              3200px long edge.  RESAMPLE 2, and the one that made
 *              4000px unrecoverable.
 *   q0.95      a browser canvas JPEG export, stepping down to 0.70,
 *              where 4:4:4 is not even on offer.
 *
 * **This spec replaces all three.** Nothing is resampled in the browser
 * and nothing is re-encoded there, so the bytes the encoder receives are
 * the bytes the camera or the scanner wrote. The geometry below is what
 * survives of those canvas calls: the same maths, stated once, applied
 * to the full-resolution original instead of to a 4200px copy of it.
 *
 * ── ⚠ THE SCALE FACTORS ARE GONE AND THAT IS THE POINT ─────────────
 * `editor.js` computed `es = Math.min(EXP/max(b.w,b.h), 1)` and then
 * `os = Math.min(OUT/max(cwd,chd), 1)`. Here both are 1 by construction,
 * because there is no intermediate canvas to fit. The rounding is kept
 * IDENTICAL to the editor's — `Math.round` at the same three places —
 * so a spec produced by the old code and one produced by the new agree
 * on the pixel rectangle wherever the old code was not already scaling.
 */

/** The colour rotation-exposed corners are filled with. `editor.js:93`. */
export const CORNER_FILL = '#12291E'

/** No rotation, no crop. What a spec means when there is nothing to say. */
export const IDENTITY_TRANSFORM = Object.freeze({
  angle: 0,
  crop: Object.freeze({ x: 0, y: 0, w: 1, h: 1 }),
})

/* The editor's minimum crop side, `editor.js:43`. A spec below this did
 * not come from the editor, and a zero-area crop is an image with no
 * pixels — which every downstream resize would divide by. */
export const MIN_CROP_SIDE = 0.12

/**
 * The bounding box of a `w x h` rectangle rotated by `deg` degrees.
 *
 * ⚠ BYTE-FOR-BYTE THE EXPRESSION FROM `editor.js:20`, deliberately. It
 * is moved rather than rewritten: this is the function whose disagreement
 * with a second copy would be invisible, so the version that has been
 * drawing the on-screen crop box since Phase 1 is the version that gets
 * to be authoritative. Returns floats — the caller rounds, and WHERE it
 * rounds is part of the spec.
 */
export function bbox(w, h, deg) {
  const r = (deg * Math.PI) / 180
  return {
    w: Math.abs(w * Math.cos(r)) + Math.abs(h * Math.sin(r)),
    h: Math.abs(w * Math.sin(r)) + Math.abs(h * Math.cos(r)),
  }
}

/**
 * Is this a transform spec at all? Returns `{ ok: true, transform }` with
 * the value normalised, or `{ ok: false, error, message }`.
 *
 * ⚠ IT REFUSES RATHER THAN REPAIRS. A crop rectangle that runs off the
 * edge could be clamped, and clamping is exactly how "the encoder cropped
 * a region nobody asked for" becomes untraceable. The editor cannot
 * produce an out-of-range spec (`editor.js:43-51` clamps every drag), so
 * one arriving here means something else wrote it.
 */
export function validateTransform(value) {
  if (value === null || value === undefined) {
    return { ok: true, transform: IDENTITY_TRANSFORM }
  }
  if (typeof value !== 'object' || Array.isArray(value)) {
    return { ok: false, error: 'invalid_transform', message: 'The transform must be an object.' }
  }

  const angle = value.angle ?? 0
  if (typeof angle !== 'number' || !Number.isFinite(angle)) {
    return { ok: false, error: 'invalid_transform', message: 'transform.angle must be a finite number of degrees.' }
  }

  const crop = value.crop ?? IDENTITY_TRANSFORM.crop
  if (typeof crop !== 'object' || crop === null || Array.isArray(crop)) {
    return { ok: false, error: 'invalid_transform', message: 'transform.crop must be an object.' }
  }
  for (const k of ['x', 'y', 'w', 'h']) {
    const v = crop[k]
    if (typeof v !== 'number' || !Number.isFinite(v)) {
      return { ok: false, error: 'invalid_transform', message: `transform.crop.${k} must be a finite number.` }
    }
  }
  if (crop.w < MIN_CROP_SIDE || crop.h < MIN_CROP_SIDE) {
    return {
      ok: false,
      error: 'invalid_transform',
      message: `transform.crop is smaller than the editor's minimum of ${MIN_CROP_SIDE} on a side.`,
    }
  }
  if (crop.x < 0 || crop.y < 0 || crop.x + crop.w > 1 || crop.y + crop.h > 1) {
    return {
      ok: false,
      error: 'invalid_transform',
      /* Named as a fraction problem rather than a pixel one: the caller
       * sent fractions and the pixels do not exist yet. */
      message: 'transform.crop runs outside the image — x, y, w and h are fractions of the ROTATED bounding box.',
    }
  }

  return {
    ok: true,
    /* Normalised so downstream code never sees a partial spec, and frozen
     * so nothing downstream can edit the thing it was handed. */
    transform: Object.freeze({
      angle,
      crop: Object.freeze({ x: crop.x, y: crop.y, w: crop.w, h: crop.h }),
    }),
  }
}

/** Is this transform a no-op? Used to skip work, never to skip checks. */
export function isIdentityTransform(t) {
  return (
    t.angle % 360 === 0 && t.crop.x === 0 && t.crop.y === 0 && t.crop.w === 1 && t.crop.h === 1
  )
}

/**
 * The pixel rectangle a spec selects, inside the ROTATED bounding box of a
 * `sourceWidth x sourceHeight` original.
 *
 * ⚠ THE ROUNDING IS THE SPEC. `editor.js` rounds the bounding box first
 * and the crop offsets second, against the ALREADY-ROUNDED box. Rounding
 * in the other order shifts the rectangle by up to a pixel, which is
 * invisible in a picture and is exactly the class of difference that
 * makes an encoder's output "nothing like the input" by degrees.
 */
export function cropRect(sourceWidth, sourceHeight, transform) {
  const b = bbox(sourceWidth, sourceHeight, transform.angle)
  const boxW = Math.round(b.w)
  const boxH = Math.round(b.h)
  const c = transform.crop

  const x = Math.round(c.x * boxW)
  const y = Math.round(c.y * boxH)
  /* Clamped to the box AFTER rounding, and only against rounding itself:
   * `validateTransform` has already refused anything genuinely outside.
   * Without this a crop of exactly w=1 can round to boxW+1 and ask for a
   * pixel that is not there. */
  const width = Math.max(1, Math.min(Math.round(c.w * boxW), boxW - x))
  const height = Math.max(1, Math.min(Math.round(c.h * boxH), boxH - y))

  return { boxWidth: boxW, boxHeight: boxH, x, y, width, height }
}

/**
 * The dimensions of the image AFTER the transform is applied and BEFORE
 * any tier resize.
 *
 * ── ⚠⚠ THIS IS THE FUNCTION THE STEP 6 DEBT IS ABOUT ────────────────
 * Jesse, 2026-08-13: *"the `verifyTierOutput` crop defect is the first
 * thing Step 6 owes."* `verifyTierOutput` accepts a tier that is either
 * at the cap or equal to THE SOURCE, and after this step "the source" is
 * no longer the file that arrived — it is what came out of here. A 4032px
 * photo cropped to a 3600px note region produces a 3600px archive tier,
 * which is neither 4000 nor 4032, and the check would refuse a correct
 * upload on every angled or margin-cropped photo. Which is most of them.
 *
 * ⚠ AND THE 4000 → 6000 MOVE OF 2026-08-20 DOES **NOT** CHANGE THIS.
 * Both branches of that rule survive a cap of any value; only the number
 * moves. *(It would have changed it: while the cap was briefly removed
 * altogether, "at the cap" stopped being an acceptable answer at all and
 * the post-transform size became mandatory on every upload for every
 * format — which `imageSize()` cannot supply for HEIC, so every iPhone
 * upload would have been refused. Keeping a cap is what kept this fix to
 * one line. Recorded because it is the reason the number was kept.)*
 */
export function transformedSize(sourceWidth, sourceHeight, transform) {
  const rect = cropRect(sourceWidth, sourceHeight, transform)
  return { width: rect.width, height: rect.height }
}
